import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExternalLink, Pause, Pencil, Play, Plus, RotateCw, Send, Settings } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';
import { useOutreachActions, useOutreachAgents, type OutreachAgent } from './api';
import { OutreachAgentFormDialog } from './OutreachAgentFormDialog';
import { linkedInMeta, statusMeta } from './mockData';

const PAGE_SIZE = 12;

function primaryCampaign(agent: OutreachAgent): string {
  return agent.assigned_campaigns[0]?.name ?? 'Non assignee';
}

function formatLastSeen(value: string | null): string {
  if (!value) return 'Jamais';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Inconnu';
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.round(diff / 60_000));
  if (minutes < 2) return 'A l instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  return `Il y a ${Math.round(hours / 24)}j`;
}

export default function OutreachAgentsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const wsId = currentWorkspace?.id;
  const [offset, setOffset] = useState(0);
  const agentsQuery = useOutreachAgents(wsId, { limit: PAGE_SIZE, offset });
  const { pauseAgent, provisionAgent, resumeAgent, syncEasypanelAgents } = useOutreachActions(wsId);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAgent, setEditingAgent] = useState<OutreachAgent | null>(null);
  const agents = agentsQuery.data?.items ?? [];
  const total = agentsQuery.data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function togglePause(agent: OutreachAgent) {
    try {
      if (agent.status === 'paused') {
        await resumeAgent.mutateAsync(agent.id);
        toast.success(`${agent.name} repris`);
      } else {
        await pauseAgent.mutateAsync(agent.id);
        toast.success(`${agent.name} en pause`);
      }
    } catch {
      toast.error("Action impossible sur l'agent");
    }
  }

  async function handleProvision(agent: OutreachAgent) {
    try {
      const result = await provisionAgent.mutateAsync(agent.id);
      if (result.provisioning?.ok === false) {
        toast.error(result.provisioning.error ?? 'Provisioning Easypanel impossible');
        return;
      }
      toast.success(`${result.item.name} provisionne`);
    } catch {
      toast.error('Provisioning Easypanel impossible');
    }
  }

  async function handleSync() {
    try {
      const result = await syncEasypanelAgents.mutateAsync();
      toast.success(`${result.total} agent${result.total > 1 ? 's' : ''} synchronise${result.total > 1 ? 's' : ''}`);
    } catch {
      toast.error('Synchronisation Easypanel impossible');
    }
  }

  if (!wsId) {
    return <div className="p-8 text-gray-500">Selectionne un workspace pour voir les agents.</div>;
  }

  return (
    <div>
      <PageHeader
        title="Agents Outreach"
        description="Creation, assignation campagne et supervision des agents OpenOutreach"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={handleSync} loading={syncEasypanelAgents.isPending}>
              <RotateCw size={14} /> Synchroniser
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus size={14} /> Creer un agent
            </Button>
          </div>
        }
      />

      {agentsQuery.isLoading && (
        <Card className="p-8 text-sm text-gray-500">Chargement des agents...</Card>
      )}

      {!agentsQuery.isLoading && agents.length === 0 && (
        <Card>
          <EmptyState
            title="Aucun agent Outreach"
            description="Cree le premier agent. Il pourra ensuite etre assigne a une campagne."
            action={
              <Button onClick={() => setShowCreate(true)}>
                <Plus size={14} /> Creer un agent
              </Button>
            }
          />
        </Card>
      )}

      {agents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {agents.map((agent) => {
            const status = statusMeta[agent.status];
            const linkedIn = linkedInMeta[agent.linkedin_status];
            const StatusIcon = status.icon;
            const quotaPercent = agent.daily_limit
              ? Math.min(100, Math.round((agent.daily_used / agent.daily_limit) * 100))
              : 0;

            return (
              <Card key={agent.id} className="p-5 flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={ROUTES.OUTREACH_AGENT_DETAIL(agent.id)}
                      className="font-heading font-bold text-brand-dark text-base hover:underline"
                    >
                      {agent.name}
                    </Link>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {agent.service_name ?? `openoutreach_${agent.name}`}
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-brand bg-brand-primary-light flex items-center justify-center shrink-0">
                    <StatusIcon size={15} className="text-brand-primary" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge tone={status.tone}>{status.label}</Badge>
                  <Badge tone={linkedIn.tone}>LinkedIn {linkedIn.label}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-400">Owner</div>
                    <div className="font-medium text-brand-dark">{agent.owner_name ?? 'A attribuer'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Proxy</div>
                    <div className="font-medium text-brand-dark">{agent.proxy_country ?? '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Uptime</div>
                    <div className="font-medium text-brand-dark">{agent.uptime_label ?? '-'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Acceptation</div>
                    <div className="font-medium text-brand-dark">{agent.acceptance_rate}%</div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Quota invitations</span>
                    <span className="font-medium text-brand-dark">
                      {agent.daily_used}/{agent.daily_limit}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-brand-primary" style={{ width: `${quotaPercent}%` }} />
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  <span className="font-medium text-brand-dark">{primaryCampaign(agent)}</span>
                  <div>Vu {formatLastSeen(agent.last_seen_at)}</div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-brand-border">
                  {agent.novnc_url ? (
                    <a href={agent.novnc_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs">
                      <Send size={12} /> Connecter
                    </a>
                  ) : (
                    <Button size="sm" variant="secondary" disabled>
                      <Send size={12} /> Connecter
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => togglePause(agent)}>
                    {agent.status === 'paused' ? <Play size={12} /> : <Pause size={12} />}
                    {agent.status === 'paused' ? 'Reprendre' : 'Pause'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleProvision(agent)}
                    loading={provisionAgent.isPending}
                  >
                    <RotateCw size={12} /> Provisionner
                  </Button>
                  <Link to={ROUTES.OUTREACH_AGENT_DETAIL(agent.id)} className="btn-ghost text-xs">
                    <Settings size={12} /> Detail
                  </Link>
                  <Button size="sm" variant="ghost" onClick={() => setEditingAgent(agent)}>
                    <Pencil size={12} /> Modifier
                  </Button>
                  {agent.novnc_url && (
                    <a href={agent.novnc_url} className="btn-ghost text-xs" target="_blank" rel="noreferrer">
                      <ExternalLink size={12} /> noVNC
                    </a>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between rounded-brand border border-brand-border bg-white px-4 py-3 text-xs text-gray-500">
          <span>
            Page {currentPage} / {totalPages} - {total} agent{total > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Precedent
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}

      <OutreachAgentFormDialog open={showCreate} onClose={() => setShowCreate(false)} workspaceId={wsId} />
      <OutreachAgentFormDialog
        open={!!editingAgent}
        onClose={() => setEditingAgent(null)}
        workspaceId={wsId}
        agent={editingAgent}
      />
    </div>
  );
}
